import {
  addToast,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { api } from "@/lib/api-client";
import { SUPPORTED_LOCALES } from "../../../../../shared/constants/locales";
import { PutCustomerNoteTag } from "@/server-types";
import { useEffect } from "react";
import { LocalizedInput } from "../LocalizedInput";
import { t } from "@lingui/core/macro";
import { LimeSwitch } from "../LimeSwitch";

export const ManageCustomerNoteTagModal = ({
  isOpen,
  onClose,
  tagId,
}: {
  isOpen: boolean;
  onClose: ({ createdTagId }: { createdTagId?: number }) => void;
  tagId?: number;
}) => {
  const { data: tagData, isFetching: isFetchingTag } =
    api.customerNote.useGetCustomerNoteTag({
      params: { tagId: tagId! },
      disabled: !isOpen || tagId == null,
    });

  const { mutateAsync: postCustomerNoteTag, isPending: isCreatingTag } =
    api.customerNote.usePostCustomerNoteTag();

  const { mutateAsync: putCustomerNoteTag, isPending: isUpdatingTag } =
    api.customerNote.usePutCustomerNoteTag();

  const form = useForm<{
    nameLocalized: PutCustomerNoteTag["body"]["nameLocalized"];
    showOnAppointment: PutCustomerNoteTag["body"]["showOnAppointment"];
  }>({
    initialValues: {
      showOnAppointment: false,
      nameLocalized: SUPPORTED_LOCALES.map((language) => ({
        language,
        value: "",
      })),
    },
    validate: {
      nameLocalized: (value) =>
        value.every((name) => name.value.trim().length === 0)
          ? t`Vnesite ime oznake`
          : null,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    form.reset();

    if (tagData) {
      const missingNameLocalized = SUPPORTED_LOCALES.flatMap((language) =>
        tagData.localizedNames.some((name) => name.language === language)
          ? []
          : [{ language, value: "" }],
      );

      const nameLocalized = tagData.localizedNames.concat(missingNameLocalized);

      form.setValues({
        nameLocalized,
        showOnAppointment: tagData.showOnAppointment,
      });
    }
  }, [isOpen, tagData]);

  const handleSubmit = async (values: typeof form.values) => {
    if (tagId) {
      await putCustomerNoteTag({
        params: { tagId: tagId! },
        body: values,
      });
      onClose({});

      addToast({
        title: t`Oznaka je bila uspešno posodobljena`,
        color: "success",
      });
    } else {
      const tag = await postCustomerNoteTag({
        body: values,
      });
      onClose({ createdTagId: tag.id });

      addToast({
        title: t`Oznaka je bila uspešno dodana`,
        color: "success",
      });
    }
  };

  const isDefaultTag = tagData?.isDefault ?? false;

  const title = (() => {
    if (tagId) {
      return <Trans>Uredi oznako zapiska strank</Trans>;
    }

    return <Trans>Dodaj oznako zapiska strank</Trans>;
  })();

  return (
    <Modal isOpen={isOpen} onClose={() => onClose({})}>
      <ModalContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            form.validate();

            if (form.isValid()) {
              handleSubmit(form.getValues());
            }
          }}
        >
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            <div>
              <LocalizedInput
                label={t`Ime oznake`}
                isDisabled={isDefaultTag}
                type="text"
                values={form.values.nameLocalized}
                handleSubmit={(values) =>
                  form.setValues({ nameLocalized: values.localizedValues })
                }
                disabled={isDefaultTag}
                error={form.errors.nameLocalized}
              />
              {isDefaultTag ? (
                <p className="mt-1 ml-1 text-xs text-gray-500">
                  <Trans>Imena privzete oznake ni mogoče urejati</Trans>
                </p>
              ) : null}
            </div>

            <LimeSwitch
              title={t`Prikaži na terminu`}
              description={t`Zapiski s to oznako bodo prikazani na terminu`}
              isCompact
              {...form.getInputProps("showOnAppointment", {
                type: "checkbox",
              })}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => onClose({})}>
              <Trans>Zapri</Trans>
            </Button>
            <Button
              type="submit"
              color="primary"
              isLoading={isUpdatingTag || isCreatingTag}
              isDisabled={isFetchingTag}
            >
              <Trans>Shrani</Trans>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
